import { useRef } from "react";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";

const BASE = "https://customer-assets-7cd3h4nn.emergentagent.net/job_yugiq-mobile-first/artifacts";

const PROJECTS = [
  { id: "shreevali", img: `${BASE}/ibqzgraf_h2it97kyc1djswrjonvx.webp`, name: "Shreevali Agro", category: "D2C · E-commerce", desc: "Farm-to-fork mango store with GI-tag storytelling, shop, export & order flows.", tags: ["Storefront", "Payments", "SEO"] },
  { id: "shubhlagna", img: `${BASE}/o10og3r3_oz54b6lmvdnqhi50lys4.webp`, name: "ShubhLagna", category: "Weddings · Editorial", desc: "Cinematic wedding-planning site with film hero, reels & consultation booking.", tags: ["Motion", "Portfolio", "Lead Gen"] },
  { id: "aaranya", img: `${BASE}/wjf5t5jo_up4aaxg7ishtxy7f94be.webp`, name: "Aaranya Igatpuri", category: "Hospitality · Resort", desc: "Immersive boutique-resort microsite with rooms, gallery & direct-booking engine.", tags: ["Booking", "Gallery", "Direct"] },
  { id: "dermaduo", img: `${BASE}/l5a3ktuz_b1rxndtixlnrz9ob87zr.webp`, name: "Derma DUO", category: "Healthcare · Aesthetics", desc: "Elegant, science-led skin clinic with treatments, gallery & WhatsApp consults.", tags: ["Clinical", "WhatsApp", "Consult"] },
  { id: "lumine", img: `${BASE}/uf0m3oxp_kotg71pwborrv08ytzhs.webp`, name: "Lumine Dental", category: "Dental · Clinic", desc: "Andheri dental studio with services, doctor profile, gallery & appointment booking.", tags: ["Reviews", "Booking", "Local SEO"] },
  { id: "horizon", img: `${BASE}/exidh471_t8od13pfkzxvnx5svctz.webp`, name: "Horizon Realty", category: "Real Estate · Advisory", desc: "Thane property advisory with verified listings, RERA proof & WhatsApp enquiries.", tags: ["Listings", "Trust", "Leads"] },
  { id: "apexmotors", img: `${BASE}/sfjmsucc_nnokv8h0fmbqhkcms6yu.webp`, name: "Apex Motors", category: "Automobile · Dealership", desc: "Premium car inventory with filters, side-by-side compare & test-drive booking.", tags: ["Inventory", "Compare", "Finance"] },
  { id: "apexacademy", img: `${BASE}/o5turw9g_dowgkmvndqepc7ztfahu.webp`, name: "Apex Academy", category: "Education · Coaching", desc: "JEE/NEET institute with results, faculty, batch fees & free demo-class funnel.", tags: ["Results", "Enroll", "Brochure"] },
  { id: "ironforge", img: `${BASE}/soziv6pc_o1ytdsbzukrk7wajh7hr.webp`, name: "Iron Forge", category: "Fitness · Gym", desc: "High-energy gym brand with bold type, BMI tools & a free-trial sign-up funnel.", tags: ["Bold", "Tools", "Free Trial"] },
];

export default function Portfolio() {
  const scroller = useRef(null);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 520);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section id="portfolio" className="relative py-20 sm:py-28 overflow-hidden" data-testid="portfolio-section">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10 sm:mb-14">
          <div className="max-w-[640px]">
            <span className="yg-eyebrow">// Selected work</span>
            <h2 className="yg-h2 text-[clamp(30px,4.6vw,52px)] mt-3 text-white">
              Websites we&apos;ve
              <br />
              <span className="text-[var(--yg-violet-light)]">designed &amp; shipped.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <p className="hidden sm:block text-[13.5px] text-[var(--yg-text-mute)] max-w-[220px]">
              {PROJECTS.length} real launches. Scroll →
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => scrollBy(-1)} aria-label="Previous" data-testid="portfolio-prev" className="w-11 h-11 rounded-full border border-[var(--yg-border-strong)] bg-white/[0.03] flex items-center justify-center text-white hover:bg-[var(--yg-violet)] hover:border-transparent transition-all">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button onClick={() => scrollBy(1)} aria-label="Next" data-testid="portfolio-next" className="w-11 h-11 rounded-full border border-[var(--yg-border-strong)] bg-white/[0.03] flex items-center justify-center text-white hover:bg-[var(--yg-violet)] hover:border-transparent transition-all">
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal scroller — bleeds to the right edge */}
      <div
        ref={scroller}
        className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4 pl-5 sm:pl-[max(2rem,calc((100vw-1240px)/2+2rem))] pr-5"
        data-testid="portfolio-scroller"
      >
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} />
        ))}
        <div className="shrink-0 w-1" aria-hidden />
      </div>

      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 mt-2 sm:hidden">
        <span className="yg-mono text-[10.5px] uppercase tracking-[0.2em] text-[var(--yg-text-dim)]">Swipe → {PROJECTS.length} projects</span>
      </div>
    </section>
  );
}

function ProjectCard({ project, index }) {
  return (
    <article
      className="group shrink-0 snap-start w-[300px] sm:w-[400px] lg:w-[440px]"
      data-testid={`portfolio-card-${project.id}`}
      style={{ animation: `yg-rise 700ms ${Math.min(index, 4) * 70}ms both cubic-bezier(.2,.9,.3,1)` }}
    >
      <div className="yg-card yg-card-hover p-1.5 sm:p-2 relative overflow-hidden">
        <div aria-hidden className="absolute -inset-6 opacity-0 group-hover:opacity-60 blur-2xl transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(108,58,237,0.3), transparent 70%)" }} />
        {/* Browser chrome */}
        <div className="relative flex items-center gap-1.5 px-2.5 py-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]/70" />
          <div className="ml-2 flex-1 h-5 rounded-md bg-white/[0.04] border border-[var(--yg-border)] flex items-center px-2">
            <span className="yg-mono text-[9.5px] text-[var(--yg-text-dim)] truncate">{project.id}.yugiq.in</span>
          </div>
        </div>
        {/* Screenshot */}
        <div className="relative rounded-[10px] overflow-hidden border border-[var(--yg-border)] aspect-[16/11]">
          <img src={project.img} alt={`${project.name} website designed by Yugiq`} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-[10px] pointer-events-none" />
        </div>
      </div>

      {/* Details */}
      <div className="px-2 pt-4">
        <div className="flex items-center gap-2">
          <span className="yg-mono text-[10.5px] tracking-[0.2em] text-[var(--yg-violet-light)]">0{index + 1}</span>
          <span className="yg-mono text-[10px] tracking-[0.2em] uppercase text-[var(--yg-text-dim)]">{project.category}</span>
        </div>
        <h3 className="mt-2 text-white font-extrabold text-[21px] tracking-[-0.02em]">{project.name}</h3>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--yg-text-mute)]">{project.desc}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {project.tags.map((t) => (
            <span key={t} className="px-2.5 py-0.5 rounded-full text-[11px] font-medium border border-[var(--yg-border-strong)] bg-white/[0.03] text-white/80">{t}</span>
          ))}
        </div>
        <a href="#audit" className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white group/link" data-testid={`portfolio-cta-${project.id}`}>
          <span className="border-b border-[var(--yg-violet)]/50 group-hover/link:border-[var(--yg-violet-light)] transition-colors pb-0.5">Build mine like this</span>
          <ArrowUpRight className="w-4 h-4 text-[var(--yg-violet-light)] group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5 transition-transform" />
        </a>
      </div>
    </article>
  );
}
